import styled from "@emotion/styled";
import Chart from "react-apexcharts";
import { EventType, DetectByHour as PropsType } from "../../types/types";
import Spinner from "../../components/spinner";
import { useEffect, useState } from "react";
import theme from "../../theme";
import { useI18n } from "../../locales";
import { getEventTypeKey } from "../../utils/langUtils";

const { md } = theme.screen;

type ChartData = {
  name: EventType;
  data: {
    x: string;
    y: number;
  }[];
};

const DetectByHour: React.FC<{
  hourData: PropsType[] | undefined;
  isGoogle?: boolean;
  hourDataIsLoading?: boolean;
  width?: string;
  height?: string;
  isReportModal?: boolean;
  isMobile?: boolean;
}> = (props) => {
  const { t } = useI18n();
  const {
    hourData,
    isGoogle,
    hourDataIsLoading,
    width,
    height,
    isReportModal,
    isMobile,
  } = props;
  const copy: PropsType[] = Object.assign([], hourData);
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [yLabelColors, setYLabelColors] = useState<string[]>([]);

  // 4시간씩 데이터 합산해주는 함수
  function sumHoursInGroups(data: ChartData[]) {
    const result: ChartData[] = [];
    data.forEach((item) => {
      const newData = [];
      for (let i = 0; i < item.data.length; i += 4) {
        const group = item.data.slice(i, i + 4);
        const sum = group.reduce((acc, curr) => acc + curr.y, 0);
        newData.push({
          x: `${group[0].x}~${group[group.length - 1].x}`,
          y: sum,
        });
      }
      result.push({ name: item.name, data: newData });
    });
    return result;
  }

  useEffect(() => {
    if (!hourData) return;
    // 차트용 데이터로 변환
    let result: ChartData[] = copy

      .filter((data) => {
        const eventKey = getEventTypeKey(data.event_type, t);
        return eventKey !== "dangerousAreaEntryEvent" && eventKey !== "theftDetection";
      })
      .map((el) => {
        const eventKey = getEventTypeKey(el.event_type, t);
        const translatedName = eventKey && eventKey !== "normal" 
          ? t.control[eventKey as keyof typeof t.control] 
          : el.event_type;
        return {
          name: translatedName as EventType,
          data: el.data.map((el) => {
            return { x: el.hour, y: el.cnt };
          }),
        };
      })
      .reverse();

    // 모바일
    if (isMobile) {
      setChartData(sumHoursInGroups(result));
    }
    // 데스크탑
    else if (!isMobile) {
      setChartData(result);
    }
  }, [isReportModal, isMobile, hourData, hourDataIsLoading, t]);

  // 시간대별 감지내역 차트 Y축 라벨 배열 생성 (마지막 데이터(차량전복)가 빨간색으로 강조되도록)
  useEffect(() => {
    if (chartData.length === 0) return;
    const arr = Array(chartData.length).fill("black");
    arr[chartData.length - 1] = "red";
    setYLabelColors(arr);
  }, [chartData]);

  // console.log("시간대별", hourData);
  // console.log("차트", chartData);
  // console.log("4시간 데이터", hourData && sumHoursInGroups(hourData));

  return (
    <Wrapper isGoogle={isGoogle}>
      {hourDataIsLoading && <Spinner />}
      {!hourDataIsLoading && hourData && (
        <>
          <Header>{t.statistics.hourlyDetections}</Header>
          <Body>
            <Chart
              options={{
                stroke: {
                  width: 3,
                },
                legend: {
                  show: false,
                },
                chart: {
                  type: "heatmap",
                  toolbar: {
                    show: false,
                  },
                },
                plotOptions: {
                  heatmap: {
                    radius: 5,
                  },
                },
                dataLabels: {
                  enabled: false,
                },
                yaxis: {
                  labels: {
                    style: {
                      // colors: ["black", "black", "black", "black", "red"],
                      colors: yLabelColors,
                      fontSize: "12px",
                      fontWeight: 600,
                    },
                  },
                },
                xaxis: {
                  labels: {
                    style: {
                      fontSize: "12px",
                    },
                  },
                },
                colors: ["#f8555a"],
              }}
              series={chartData}
              type="heatmap"
              height={height ? height : 300}
              width={width ? width : 500}
            />
            <ChartUnit>{t.statistics.timeUnit}</ChartUnit>
          </Body>
        </>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div<{ isGoogle?: boolean }>`
  min-width: 534px;
  max-width: 534px;
  /* width: 534px; */
  background: #f9f9f9;
  box-shadow: ${({ isGoogle }) =>
    !isGoogle && `0px 4px 10px rgba(0, 0, 0, 0.25)`};
  border: ${({ isGoogle }) => isGoogle && `3px solid #ececec`};
  border-radius: 15px;
  height: 100%;
  padding: 20px;
  display: flex;
  flex-direction: column;
  height: 382px;

  @media (max-width: ${md}px) {
    ${({ isGoogle }) =>
      !isGoogle &&
      `
        border-radius: 5px;
    background: ${theme.color.white};
    width: 100%;
    min-width: 100%;
    max-width: 100%;
    box-shadow: none;
    height: auto;
    min-height: 250px;
    max-height: none;
    padding: 14px;

     tspan {
      font-size: ${theme.fontSize.FONT_3};
      font-weight: ${theme.fontWeight.NORMAL};
    }
    
    `}
  }
`;

const Header = styled.h1`
  font-weight: 700;
  font-size: 15px;
  line-height: 18px;
  color: #484848;
  margin-bottom: 20px;

  @media (max-width: ${md}px) {
    font-size: ${theme.fontSize.FONT_1};
    font-weight: ${theme.fontWeight.SEMI_BOLD};
    margin-bottom: 12px;
  }
`;

const Body = styled.div`
  position: relative;
  background: #fefefe;
  border-radius: 10px;
  flex: 1;
`;

const ChartUnit = styled.span`
  position: absolute;
  bottom: 5px;
  left: 5px;
  font-size: 10px;
`;

export default DetectByHour;
